import { clsx } from 'clsx'

export type QuestionChipState = 'correct' | 'partial' | 'incorrect' | 'unanswered' | 'pending'

export interface QuestionChipItem {
  quizId: string
  label: string
  state: QuestionChipState
}

interface Props {
  items: QuestionChipItem[]
  selectedQuizId: string
  onSelect: (quizId: string) => void
  ariaLabel?: string
}

const STATE_CLASS: Record<QuestionChipState, string> = {
  correct: 'bg-green-50 text-green-700 border-green-300 hover:border-green-500',
  partial: 'bg-amber-50 text-amber-700 border-amber-300 hover:border-amber-500',
  incorrect: 'bg-red-50 text-red-700 border-red-300 hover:border-red-500',
  unanswered: 'bg-gray-50 text-gray-600 border-gray-300 hover:border-gray-500',
  // G8 — 교재 풀이 후 입력됨, 강사 채점 대기.
  pending: 'bg-amber-50 text-amber-700 border-amber-300 hover:border-amber-500',
}

const STATE_ICON: Record<QuestionChipState, string> = {
  correct: '✓',
  partial: '△',
  incorrect: '✗',
  unanswered: '−',
  pending: '⏳',
}

export function QuestionChipGrid({ items, selectedQuizId, onSelect, ariaLabel }: Props) {
  return (
    <div
      className="flex flex-wrap gap-2"
      role="group"
      aria-label={ariaLabel}
      data-testid="quiz-chip-grid"
    >
      {items.map((it) => {
        const isSelected = it.quizId === selectedQuizId
        return (
          <button
            key={it.quizId}
            type="button"
            onClick={() => onSelect(it.quizId)}
            data-testid="quiz-question-chip"
            data-state={it.state}
            aria-pressed={isSelected}
            className={clsx(
              'inline-flex items-center gap-1 min-w-[44px] h-9 px-2.5 rounded-lg text-sm font-semibold border-2 transition-all',
              STATE_CLASS[it.state],
              isSelected && 'ring-2 ring-offset-1 ring-sky-400',
            )}
          >
            <span>{it.label}</span>
            <span aria-hidden>{STATE_ICON[it.state]}</span>
          </button>
        )
      })}
    </div>
  )
}
